import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "components/card";
import api from "services/api";
import { MdArrowBack, MdAnimation, MdPlayCircle } from "react-icons/md";

const AnimationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMovie = async () => {
      try {
        setLoading(true);
        const data = await api.getTitle(id);
        setMovie(data);
      } catch (e) {
        console.error("Failed to load animation title:", e);
      } finally {
        setLoading(false);
      }
    };
    if (id) loadMovie();
  }, [id]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center pt-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center pt-20">
        <p className="text-xl font-bold text-navy-700 dark:text-white">Animation title not found.</p>
        <button onClick={() => navigate("/admin/animation")} className="mt-4 text-brand-500 hover:underline">
          Return to Animation
        </button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {/* Header / Back Button */}
      <div className="mt-3 flex items-center justify-between">
        <button 
          onClick={() => navigate("/admin/animation")}
          className="flex items-center gap-2 rounded-xl bg-lightPrimary px-4 py-2 text-sm font-bold text-gray-600 transition hover:bg-gray-200 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
        >
          <MdArrowBack className="h-5 w-5" /> Back to Animation
        </button>
      </div>

      <Card extra="w-full overflow-hidden p-6 relative">
        <div className="flex flex-col gap-8 md:flex-row">
          {/* Poster Section */}
          <div className="w-full shrink-0 md:w-1/3 lg:w-1/4">
            <div className="relative aspect-[2/3] w-full overflow-hidden rounded-2xl shadow-xl">
              {movie.posterUrl ? (
                <img src={movie.posterUrl} alt={movie.title} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-100 dark:bg-navy-700">
                  <MdAnimation className="h-20 w-20 text-gray-300" />
                </div>
              )}
            </div>
          </div>

          {/* Details Section */}
          <div className="flex w-full flex-col justify-center">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-brand-500/10 px-3 py-1 text-sm font-bold text-brand-500">
                {movie.year}
              </span>
              {(movie.genres || []).map((genre) => (
                <span key={genre} className="rounded-full border border-gray-200 bg-lightPrimary px-3 py-1 text-sm font-medium text-navy-700 dark:border-white/10 dark:bg-navy-800 dark:text-white">
                  {genre}
                </span>
              ))}
            </div>

            <h1 className="mb-2 text-4xl font-bold text-navy-700 dark:text-white">
              {movie.title}
            </h1>
            
            <div className="mb-6 text-base font-medium text-gray-600 dark:text-gray-400">
              VJ: <span className="text-brand-500">{movie.vj || "None"}</span>
            </div>

            <div className="mb-8 max-w-3xl">
              <h3 className="mb-2 text-lg font-bold text-navy-700 dark:text-white">Overview</h3>
              <p className="text-base leading-relaxed text-gray-600 dark:text-gray-400">
                {movie.overview || "No overview available."}
              </p>
            </div>

            {movie.videoUrl && (
              <div>
                <a 
                  href={movie.videoUrl} 
                  target="_blank" 
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-[20px] bg-brand-500 px-6 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 shadow-lg shadow-brand-500/30"
                >
                  <MdPlayCircle className="h-6 w-6" /> Play
                </a>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AnimationDetails;
